/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2016, 2025
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../widgets/livewidget/nls/StringResources',
	'../../../util/DashboardFormatter',
	'../../../util/ArrayUtils',
	'./GridEdgeIterator',
	'./Summary',
	'../../../extensions/client-summaries/content-features/SummaryTypes',
	'../../../widgets/livewidget/query/QueryResultDataUtils'
], function (
	StringResources,
	Formatter,
	ArrayUtils,
	GridEdgeIterator,
	Summary,
	SummaryTypes,
	QueryResultDataUtils
) {

	'use strict';

	const SUMMARY_ID = StringResources.get('summaryCaption');
	const NOT_AVAILABLE = StringResources.get('value_is_not_available');
	const UNDEFINED = 'undefined';
	const NONE = 'none';
	const OBJECT = 'object';
	const OVERALL = 'overall';

	const _getUseValuePrivate = oValue => (oValue && typeof oValue.value !== UNDEFINED) ? oValue.value : oValue;

	const getKeys = (aData, bData) => {
		const aKeys = [];
		if (Array.isArray(aData)) {
			for (let i = 0; i < aData.length; i++) {
				aKeys.push(_getUseValuePrivate(aData[i]));
			}
		}

		if (typeof bData !== UNDEFINED) {
			aKeys.push(_getUseValuePrivate(bData));
		}
		return aKeys.toString();
	};

	/**
	 * !!! PERFORMANCE WARNING !!!
	 * This class manipulates potentially very large amounts of data on the client. Any line of code may be
	 * executed millions of times to render a single visualization, so should be as optimized as possible.
	 */
	class GridDataProcessor {
		constructor(options) {
			this._queryResult = options.queryResult;
			this._numberOfColumns = options.numberOfColumns || 0;
			// this column and every index after it is not grouped (fact indexes)
			this._firstNonGroupedColumnIndex = options.lastGroupedColumnIndex || 0;
			this._ungroupedColumnsInfo = [];
			this._rankDataItems = options.rankDataItems || [];
			this._columnSlot = options.columnSlot;
			this._columnDataItems = options.columnDataItems || [];
			this._getRankFormat = options.getRankFormat;
			this._getRealColumnIndex = options.getRealColumnIndex;
			this._valuesMap = {};
			this._exceedsColumnThreshold = options.exceedsColumnThreshold || false;
			this._numberOfAttributeColumns = this._numberOfColumns - (options.factColumnIndices ? options.factColumnIndices.length : 0);
			this._doSummary = options.doSummary;
			this.summaryAPI = options.summaryFeature;
			this.summaryNotSupported = this.summaryAPI && this.summaryAPI.isNotSupported();
			this._onCellDataChange = options.onCellDataChange;
			this._heatDataItems = options.heatDataItems || [];
			this._numberOfHeatColumns = this._heatDataItems.length;

			this._summaryValuePromises = [];

			this._factColumnsMap = {};
			Array.isArray(options.factColumnIndices) && options.factColumnIndices.forEach(index => this._factColumnsMap[index] = true);
		}

		/**
		 * onCellDataChange callback wrapper for formatted values
		 * @param {number} row row index
		 * @param {number} col column index
		 * @param {string} value cell value
		 * @param {object} formatSpec format specification for formatting the value
		 */
		onCellDataChange(row, col, value, formatSpec) {
			// CADBLOC-1398: summary value should show N/A when it is not available
			const rawValue = value && value.u ? value.u : value || NOT_AVAILABLE;
			this._onCellDataChange(row, col, Formatter.format(rawValue, formatSpec || null), rawValue);
		}

		_setUngroupedColumnsInfo() {
			let dataItem;
			let info;
			let columnIndex;
			// add column info
			for (columnIndex = this._firstNonGroupedColumnIndex; columnIndex < this._numberOfColumns; columnIndex++) {
				dataItem = this._columnDataItems[columnIndex];
				const isRankDataItem = dataItem.isRank && dataItem.isRank();
				info = {
					'dataItemUniqueId': dataItem.getId(),
					'formatSpec': this._getSlotFormat(columnIndex),
					'id': dataItem.getId(),
					'index': columnIndex,
					'aggregationType': dataItem.getAggregation(),
					'addSummary': !isRankDataItem && this._getSlotDataItemList(this._columnSlot)[this._getRealColumnIndex(columnIndex)].getAggregation() !== NONE
				};
				this._ungroupedColumnsInfo.push(info);
			}
			// add heat column info
			for (columnIndex = this._numberOfColumns; columnIndex < this._numberOfColumns + this._numberOfHeatColumns; columnIndex++) {
				dataItem = this._heatDataItems[columnIndex - this._numberOfColumns];
				info = {
					'dataItemUniqueId': dataItem.getId(),
					'id': dataItem.getId(),
					'index': columnIndex,
					'aggregationType': dataItem.getAggregation(),
					'addSummary': false
				};
				this._ungroupedColumnsInfo.push(info);
			}
		}

		_getSlotFormat(index) {
			let formatSpec;
			let dataItemList = this._getSlotDataItemList(this._columnSlot);
			if (this._rankDataItems.length) {
				if (this._columnDataItems[index].isRank) {
					formatSpec = this._getRankFormat();
				} else {
					formatSpec = dataItemList[index - this._rankDataItems.length].getFormat();
				}
			} else {
				formatSpec = dataItemList[index].getFormat();
			}
			return formatSpec;
		}

		_getSlotDataItemList(slot) {
			if (this._queryResult.getSlotDataItemList) {
				// _queryResult is an instance of GridNullSuppressionProvider.
				// This occurs when a v1 query is used to get table data.

				// Use GridNullSuppressionProvider::getSlotDataItemList to get data items
				// rather than directly from query.
				return this._queryResult.getSlotDataItemList(slot) || [];
			} else {
				// _queryResult is an instance of QueryResultAPI.
				// This occurs when a v2 query is used to get table data.
				// Return data items directly from slot.
				// At this time, null suppression has not yet been implemented for v2 -
				// changes to JQGrid may be required to support it. (Hopefully not).
				// Either way, when the v2Table feature flag is removed, only this clause should remain.
				// TODO - See epic CADBLW-8122.
				return (slot && slot.getDataItemList()) || [];
			}
		}

		_generateNewDataTableWithOrder() {
			if (this._doSummary) {
				return this._preProcessedData.length ? this._preProcessedData : this._rawData;
			}

			// Build list of data item indices which together form the basis of a tuple.
			const tuplePartIndexList = this._columnDataItems.map(
				(dataItem, index) => (dataItem.getType() === 'attribute' ? index : -1)
			).filter(index => index >= 0);

			// Given a tuple, produce a unique string which can identify it.
			const getTupleString = (tuple) => tuple.map(
				({ value }) => value
			).join('x');

			// Get indices of rows in _preProcessedData
			const preProcessedDataTupleIndexMap = {};
			this._preProcessedData.forEach(
				(preProcessedDataRow, i) => {
					const tuplePartList = tuplePartIndexList.map(
						tuplePartIndex => preProcessedDataRow[tuplePartIndex].value
					);
					const tupleString = getTupleString(tuplePartList);
					preProcessedDataTupleIndexMap[tupleString] = i;
				}
			);
			// Copy _preProcessedData to _rawData in the order already in _rawData.
			for (let rowIndex = 0; rowIndex < this._rawData.length; rowIndex++) {
				const queryDataRow = this._rawData[rowIndex];
				const tuplePartList = tuplePartIndexList.map(
					tuplePartIndex => queryDataRow[tuplePartIndex][0]
				);
				const tupleString = getTupleString(tuplePartList);

				const matchingIndex = preProcessedDataTupleIndexMap[tupleString];
				if (matchingIndex !== undefined) {
					this._rawData[rowIndex] = this._preProcessedData[matchingIndex];
					this._preProcessedData[matchingIndex] = null;
				}
			}
			return this._rawData;
		}

		getData() {
			this._rawData = QueryResultDataUtils.getFlatResolvedDataRows(this._queryResult);
			if (this._exceedsColumnThreshold) {
				// if ranking is applied or summary is disabled, we should respect the sort order of the query result
				return Promise.resolve(this._rawData);
			} else {
				// Promise-ifying this as a chain to allow time between the expensive steps for
				// the rest of the UI to react.  Long-running
				return Promise.resolve()
					.then(this._setUngroupedColumnsInfo.bind(this))
					.then(this._generateIntermediateObjects.bind(this))
					.then(this._generateNewDataTableWithSummaries.bind(this))
					.then(this._generateNewDataTableWithOrder.bind(this));
			}
		}

		/*
		 * Generates grouped columns traversal object and a values map containing summaries.
		 */
		_generateIntermediateObjects() {
			//an intermediate object which the iterator will traverse to get grouped column and summaries info
			//used to generate the keys for indexing into the values map.
			const dataRowCount = this._queryResult.getRowCount();
			let container = {};
			let tuple;

			//Add details to the table.
			for (let rowIndex = 0; rowIndex < dataRowCount; rowIndex++) {
				tuple = this._getTuple(rowIndex);
				this._addTupleToGroupedItemsContainer({ container, tuple, rowIndex });
			}

			//Add summaries.
			if (this._doSummary) {
				this._addSummariesToGroupedItemsContainer({ container });
			}

			for (let rowIndex = 0; rowIndex < dataRowCount; rowIndex++) {
				tuple = this._getTuple(rowIndex);
				this._generateIntermediateObjectsForUngroupedColumns(tuple, rowIndex);
			}
			return this._addSortKeysToContainer(container);
		}

		_generateIntermediateObjectsForUngroupedColumns(tuple, rowIdx) {
			let ungroupedColumnInfo, value, rowValue, cellIdx, key, useValue;
			for (let idx = 0, j = this._firstNonGroupedColumnIndex; j < this._numberOfColumns + this._numberOfHeatColumns; j++, idx++) {
				ungroupedColumnInfo = this._ungroupedColumnsInfo[idx];
				value = GridDataProcessor.getCellValue(this._queryResult, rowIdx, j);
				cellIdx = `${rowIdx}:${j}`;
				if (!GridDataProcessor.isUndefined(value)) {
					useValue = GridDataProcessor.getUseValue(value);
					if (!GridDataProcessor.isUndefined(useValue) && useValue !== null) {
						rowValue = tuple.concat(ungroupedColumnInfo.dataItemUniqueId);
						key = getKeys(rowValue);
						this._valuesMap[key] = { value, cellIdx };
						if (ungroupedColumnInfo.addSummary && ungroupedColumnInfo.aggregationType) {
							this._createSummaryCellsIfApplicable(rowValue, useValue, ungroupedColumnInfo);
						}
					}
				}
			}
		}

		/**
		 * Retrieve a cell value for grid
		 *
		 * @param {number} rowIdx - the row index value
		 * @param {number} columnIdx - the column index value
		 *
		 * @return {object} the cell value object
		 */
		static getCellValue(queryResult, rowIdx, columnIdx) {
			let value = queryResult.getValue(rowIdx, columnIdx);
			return ((Array.isArray(value)) && !!value.length) ? value[0] : value;
		}

		static getUseValue (value) {
			return _getUseValuePrivate(value);
		}

		static isUndefined (value) {
			return typeof value === UNDEFINED;
		}

		_getTuple(rowIdx) {
			let tuple = [];
			for (let colIdx = 0; colIdx < this._numberOfColumns; colIdx++) {
				if (!this._factColumnsMap[colIdx]) {
					tuple.push(GridDataProcessor.getCellValue(this._queryResult, rowIdx, colIdx));
				}
			}
			return tuple;
		}

		/**
		 * Returns a new object with Summary as the  last item to be used for iterating
		 */
		_addSortKeysToContainer(edge) {
			let hasSummary = false;
			const newEdgeObject = {
				edge,
				sortedKeys: []
			};

			for (let key in edge) {
				if (edge.hasOwnProperty(key)) {
					if (key === SUMMARY_ID) {
						hasSummary = true;
					} else {
						newEdgeObject.sortedKeys.push(key);
						let nestedEdges = edge[key].nestedEdges;
						if (nestedEdges) {
							edge[key].nestedEdges = this._addSortKeysToContainer(nestedEdges);
						}
					}
				}
			}
			if (hasSummary) {
				newEdgeObject.sortedKeys.push(SUMMARY_ID);
				let nestedEdges = edge[SUMMARY_ID].nestedEdges;
				if (nestedEdges) {
					edge[SUMMARY_ID].nestedEdges = this._addSortKeysToContainer(nestedEdges);
				}
			}
			return newEdgeObject;
		}

		_addTupleToGroupedItemsContainer({ container, tuple, rowIndex, columnIndex, tuplePartIndex = 0 }) {
			columnIndex = columnIndex || 0;
			if (tuple && tuple.length > 0) {
				let tuplePart = tuple[tuplePartIndex].value;
				if (!container[tuplePart]) {
					container[tuplePart] = {
						value: tuple[tuplePartIndex],
						level: columnIndex,
						cellIdx: `${rowIndex}:${columnIndex}`
					};
				}

				const nextTuplePartIndex = tuplePartIndex + 1;
				if (nextTuplePartIndex < tuple.length) {
					if (!container[tuplePart].nestedEdges) {
						container[tuplePart].nestedEdges = {};
					}
					this._addTupleToGroupedItemsContainer({
						container: container[tuplePart].nestedEdges,
						tuple,
						rowIndex,
						columnIndex: columnIndex + 1,
						ancestor: tuplePart,
						tuplePartIndex: nextTuplePartIndex
					});
				}
			}
		}

		_addSummaryToGroupedItemsContainer(container, columnIndex, ancestor, bIsOverallSummary) {
			let isOverallSummary = bIsOverallSummary || columnIndex === 0;
			if (columnIndex < this._numberOfAttributeColumns) {
				let summaryNode = {
					isSummary: true,
					level: columnIndex,
					isOverallSummary: isOverallSummary,
					summaryOf: (isOverallSummary) ? OVERALL : ancestor
				};

				container[SUMMARY_ID] = summaryNode;

				if (columnIndex < (this._numberOfAttributeColumns - 1)) {
					//special handling for overall summary in order for the crosstab to render the summary column correctly
					//need to add as many summary as nested levels
					summaryNode.nestedEdges = {};
					this._addSummaryToGroupedItemsContainer(summaryNode.nestedEdges, columnIndex + 1, ancestor, isOverallSummary);
				}
			}
		}

		//This function is called to add the summaries when all of the details are known.  This allows us to optimize out redundant summaries generated
		//where single values occur in a particular branch.  This can significantly reduce the size of the output for deeply nested 'inverted data hierarchies'
		//where details come before parents as in RTC247618 - optimizes [number of columns]*rowLimit to 2*rowLimit + 2 worst case.
		_addSummariesToGroupedItemsContainer({ container, columnIndex, ancestor, parentSingletonSummaryAdded=false }) {
			columnIndex=columnIndex || 0;
			const columnValueKeys = Object.keys(container);
			columnValueKeys.forEach((columnValueKey) => {
				if (columnValueKeys.length>1 || !parentSingletonSummaryAdded) {
					//Optimization: Avoid replicating 'redundant summaries' by pushing them as high in the nesting as possible. ie: if a parent
					//summary is for a single value, don't replicate the same summary in nested branches that also have one detail
					parentSingletonSummaryAdded = (columnValueKeys.length===1);
					this._addSummaryToGroupedItemsContainer(container, columnIndex, ancestor);
				}
				if (container[columnValueKey].nestedEdges) {
					this._addSummariesToGroupedItemsContainer({
						container: container[columnValueKey].nestedEdges,
						columnIndex: columnIndex+1,
						ancestor: columnValueKey,
						parentSingletonSummaryAdded
					});
				}
			});
		}

		/**
		 * caching the summary value promise
		 *
		 * @param {object} summary value promise; when resolved the summary value is available
		 */
		cacheSummaryValuePromise(promise) {
			this._summaryValuePromises.push(promise);
		}

		/**
		 * Returns a promise indicating whether all summaries get in the grid
		 */
		whenAllSummaryValuesAreReady() {
			return Promise.all(this._summaryValuePromises);
		}

		_getSummaryKeys(tuple) {
			const summaryKeys = {};
			//rowStart/rowEnd indicates starting/ending position in the originalDimension array that contains row info
			let rowEnd = this._numberOfAttributeColumns - 1;
			for (let rowStart = 0; rowEnd >= rowStart; --rowEnd) {
				tuple[rowEnd] = SUMMARY_ID;
				summaryKeys[getKeys(tuple)] = true;
			}
			return Object.keys(summaryKeys);
		}

		_generateNewDataTableWithSummaries(groupedColumnsTraversalObject) {
			let dataWithSummary = [];
			let nextGroupedItem;
			let dataTableRow;
			let rowIndex = 0;

			let groupedColumnsRowIterator = new GridEdgeIterator(groupedColumnsTraversalObject, undefined);
			while ((nextGroupedItem = groupedColumnsRowIterator.next())) {
				dataTableRow = [];
				this._addGroupedItemsToRow(dataTableRow, nextGroupedItem);
				this._addValuesToRow(dataTableRow, nextGroupedItem, ++rowIndex);
				dataWithSummary.push(dataTableRow);
			}
			this._preProcessedData = dataWithSummary;
		}

		_addGroupedItemsToRow(dataTableRow, firstGroupedItem) {
			let nextColumn = firstGroupedItem;
			let columnIndex = 0;
			while (nextColumn && (columnIndex < this._firstNonGroupedColumnIndex)) {
				dataTableRow.push(nextColumn);
				nextColumn = nextColumn.subCategory;
				columnIndex++;
			}
		}

		/*
		 *  This function adds fact value or attribute value which is not grouped.
		 */
		_addValuesToRow(dataTableRow, firstGroupedItem, rowIndex) {

			for (let columnIndex = 0; columnIndex < this._ungroupedColumnsInfo.length; columnIndex++) {
				let nextColumn = this._ungroupedColumnsInfo[columnIndex];

				let key = getKeys(firstGroupedItem.descendant_or_self, nextColumn.dataItemUniqueId);
				let valueObj = this._valuesMap[key] || null;
				let data = null;
				let rawValue = null;
				if (valueObj) {
					if (valueObj.getSummarizedValue) {
						if (!valueObj._isLocalSummary) {
							data = valueObj.getSummarizedValue(rowIndex, nextColumn.index);
						} else if (valueObj.getValue() !== null) {
							rawValue = valueObj.getValue();
							data = Formatter.formatNull(rawValue, valueObj._formatSpec || null);
						} else {
							data = valueObj.value;
						}
					} else {
						data = valueObj.value;
					}
				}

				let result = data;

				if (!result) {
					//for summary of attribute column which is ungrouped
					if (firstGroupedItem.isSummary && !nextColumn.aggregationType) {
						result = '  ';
					}
				}

				dataTableRow.push({
					isSummary: firstGroupedItem.isSummary,
					value: result,
					rawValue: rawValue,
					key: key,
					cellIdx: (valueObj) ? valueObj.cellIdx : null,
					isOverallSummary: firstGroupedItem.isOverallSummary
				});
			}
		}

		_isLocalSummary(aggregationType) {
			return SummaryTypes.indexOf(aggregationType) === -1;
		}

		/**
		 * Create a Summary Cell to hold data for each summary level/measure at control breaks.
		 * Summary cell values will be updated as data is processed later on (stored by key in the valuesMap)
		 * @param {number} rowValue a tuple followed by the dataItemId of a single measure
		 * @param {number} useValue cell's use value
		 * @param {object} ungroupedColumnInfo info for non grouped column
		 *
		 * example: a tuple involving Studio,Genre,Film and measure Domestic
		 *	Tuple "Fox","Action","Avatar", idDomestic
		 *	If we haven't processed data yet, we'll create an object to hold the summaries and store into a map for keys:
		 * 	summary: "Fox", "Action", "Summary", idDomestic
		 *	summary: "Fox", "Summary", "Summary", idDomestic
		 *	summary "Summary", "Summary", "Summary", idDomestic
		 *
		 *	Any tuples at that match "Fox","Action", "X" for that measure will yield no new summaries
		 *	When a new tuple is encountered "Fox", "Animation", "X" there is no match for "Fox", "Animation", "Summary", idDomestic
		 *	so a new summary cell will be created etc.
		 */
		_createSummaryCellsIfApplicable(rowValue, useValue, ungroupedColumnInfo) {
			let end, summaryKey, currentSummaryValue;

			const keyUIds = {
				summaryKeys: [],
				oCategoryAndTupleUIds: {},
				isLocalSummary: this._isLocalSummary(ungroupedColumnInfo.aggregationType)
			};

			// groups not including the last attribute and measure
			end = rowValue.length - 2;
			for (; end >= 0; --end) {
				rowValue[end] = SUMMARY_ID;
				summaryKey = getKeys(rowValue);
				keyUIds.summaryKeys.push(summaryKey);
				const categoryAndTupleUId = {
					categoryUIds: [],
					tupleUIds: []
				};

				if (!(keyUIds.isLocalSummary || this.summaryNotSupported || keyUIds.oCategoryAndTupleUIds[summaryKey])) {
					for (let i = 0; i < this._columnDataItems.length; i++) {
						if (typeof rowValue[i] === OBJECT) {
							categoryAndTupleUId.categoryUIds.push(this._columnDataItems[i].getId());
							categoryAndTupleUId.tupleUIds.push(rowValue[i].value);
						}
					}
				}
				keyUIds.oCategoryAndTupleUIds[summaryKey] = categoryAndTupleUId;

				currentSummaryValue = this._valuesMap[summaryKey];
				if (!currentSummaryValue && ungroupedColumnInfo) {
					currentSummaryValue = new Summary(ungroupedColumnInfo, keyUIds.isLocalSummary);
					if (!this.summaryNotSupported && !keyUIds.isLocalSummary) {
						// Add call back context to update values once resolved
						currentSummaryValue.addCallBackContext({
							categoryUIds: keyUIds.oCategoryAndTupleUIds[summaryKey].categoryUIds,
							tupleUIds: keyUIds.oCategoryAndTupleUIds[summaryKey].tupleUIds,
							cbOnCellDataChange: this.onCellDataChange.bind(this),
							cbGetSummary: this.summaryAPI ? this.summaryAPI.getSummary.bind(this.summaryAPI) : undefined,
							cbAddSummaryValuePromise: this.cacheSummaryValuePromise.bind(this)
						});
					}
				}

				if (currentSummaryValue) {
					currentSummaryValue.addValue(useValue);
				}
				this._valuesMap[summaryKey] = currentSummaryValue;

			}
		}
	}

	return GridDataProcessor;
});
